<template>
  <v-dialog v-model="internalValue" max-width="760px" persistent scrollable>
    <v-card class="ds-dialog">
      <!-- Header -->
      <v-card-title class="ds-dialog-header">
        <v-icon color="white" class="mr-2" style="position: relative; z-index: 1;"
          >mdi-microphone-message</v-icon
        >
        <span class="ds-dialog-title">Revisar itens ditados</span>
        <v-spacer></v-spacer>
        <v-btn icon class="close-btn" color="white" @click="handleCancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="ds-dialog-body">
        <v-alert type="info" text dense class="mb-3">
          Confira os {{ typeLabel }} reconhecidos por voz antes de salvar. Você pode
          editar, completar ou remover cada item.
        </v-alert>

        <div v-if="localItems.length === 0" class="pa-6 text-center text-caption">
          Nenhum item para revisar.
        </div>

        <section
          v-for="(item, index) in localItems"
          :key="index"
          class="ds-form-section"
        >
          <div class="ds-form-section__head">
            <div class="ds-form-section__title">
              <v-icon small color="primary" class="mr-2">{{ typeIcon }}</v-icon>
              <span>Item {{ index + 1 }}</span>
            </div>
            <div class="d-flex align-center">
              <!-- Match TUSS -->
              <v-chip
                v-if="item.tuss_code"
                x-small
                color="success"
                outlined
                class="mr-1"
              >
                TUSS {{ item.tuss_code }}
                <span v-if="item.score != null" class="ml-1"
                  >· {{ formatScore(item.score) }}</span
                >
              </v-chip>
              <v-chip
                v-else-if="prescriptionType !== 'supplement'"
                x-small
                color="warning"
                outlined
                class="mr-1"
              >
                sem correspondência
              </v-chip>
              <v-btn icon small color="error" @click="removeItem(index)">
                <v-icon size="18">mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </div>

          <div
            v-if="item.matchName && item.matchName !== item.name"
            class="mb-2 text-caption text--secondary"
          >
            Falado: "{{ item.raw || item.name }}" → base: "{{ item.matchName }}"
          </div>

          <v-row dense>
            <!-- Nome (sempre) -->
            <v-col cols="12">
              <v-text-field
                v-model="item.name"
                :label="`Nome do ${typeLabelSingular} *`"
                outlined
                dense
                color="primary"
                hide-details="auto"
              >
                <template v-slot:prepend-inner>
                  <v-icon size="18">{{ typeIcon }}</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Campos de posologia: só medicamento/suplemento -->
            <template v-if="prescriptionType !== 'exam'">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="item.dosage"
                  label="Dosagem"
                  placeholder="ex.: 50mg"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                ></v-text-field>
              </v-col>

              <!-- Quantidade: número + unidade (igual ao fluxo "@") -->
              <v-col cols="4" md="2">
                <v-text-field
                  v-model="item.quantidade_num"
                  label="Qtd"
                  type="number"
                  min="0"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                ></v-text-field>
              </v-col>

              <v-col cols="8" md="6">
                <v-combobox
                  v-model="item.quantidade_unidade"
                  :items="unidadesOptions"
                  label="Unidade"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                ></v-combobox>
              </v-col>

              <v-col cols="6" md="4">
                <v-combobox
                  v-model="item.frequencia"
                  :items="frequenciaOptions"
                  label="Frequência"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                ></v-combobox>
              </v-col>

              <v-col cols="6" md="4">
                <v-combobox
                  v-model="item.duracao"
                  :items="duracaoOptions"
                  label="Duração"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                ></v-combobox>
              </v-col>

              <v-col cols="12" md="4">
                <v-combobox
                  v-model="item.via_administracao"
                  :items="viaAdministracaoOptions"
                  label="Via de administração"
                  outlined
                  dense
                  color="primary"
                  hide-details="auto"
                ></v-combobox>
              </v-col>
            </template>

            <!-- Observações (sempre) -->
            <v-col cols="12">
              <v-text-field
                v-model="item.observacoes"
                label="Observações"
                outlined
                dense
                color="primary"
                hide-details="auto"
              ></v-text-field>
            </v-col>

            <!-- Diagnósticos (CID) deste item -->
            <v-col cols="12">
              <PatientCidSelector
                v-if="patientId"
                v-model="item.condition_ids"
                :patient-id="patientId"
              />
            </v-col>
          </v-row>
        </section>
      </v-card-text>

      <v-card-actions class="ds-dialog-actions">
        <v-chip small outlined :color="typeColor">
          <v-icon small left>{{ typeIcon }}</v-icon>
          {{ localItems.length }} {{ typeLabel }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="handleCancel">
          <v-icon left>mdi-close</v-icon>
          Cancelar
        </v-btn>
        <v-btn
          color="primary"
          depressed
          :disabled="localItems.length === 0 || !allNamed"
          @click="handleConfirm"
        >
          Adicionar à prescrição
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  frequenciaOptions,
  duracaoOptions,
  viaAdministracaoOptions,
} from "@/features/patients/prescriptions/utils/medicationOptions.js";
import PatientCidSelector from "@/features/patients/prescriptions/components/PatientCidSelector.vue";

export default {
  name: "DictationReviewDialog",

  components: { PatientCidSelector },

  props: {
    value: { type: Boolean, default: false },
    items: { type: Array, default: () => [] },
    prescriptionType: { type: String, default: "medication" },
    patientId: { type: [String, Number], default: null },
  },

  data() {
    return {
      frequenciaOptions,
      duracaoOptions,
      viaAdministracaoOptions,
      unidadesOptions: [
        "comprimido(s)",
        "cápsula(s)",
        "gota(s)",
        "ml",
        "ampola(s)",
        "sachê(s)",
      ],
      localItems: [],
    };
  },

  computed: {
    internalValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    allNamed() {
      return this.localItems.every((i) => i.name && i.name.trim().length > 0);
    },
    typeColor() {
      return (
        { medication: "primary", exam: "success", supplement: "warning" }[
          this.prescriptionType
        ] || "primary"
      );
    },
    typeIcon() {
      return (
        { medication: "mdi-pill", exam: "mdi-test-tube", supplement: "mdi-leaf" }[
          this.prescriptionType
        ] || "mdi-pill"
      );
    },
    typeLabel() {
      return (
        { medication: "medicamentos", exam: "exames", supplement: "suplementos" }[
          this.prescriptionType
        ] || "itens"
      );
    },
    typeLabelSingular() {
      return (
        { medication: "medicamento", exam: "exame", supplement: "suplemento" }[
          this.prescriptionType
        ] || "item"
      );
    },
  },

  watch: {
    value(open) {
      if (open) {
        // Clona os itens recebidos para edição local (não muta o pai).
        // Garante o array de CIDs por item.
        this.localItems = (this.items || []).map((i) => ({
          ...i,
          condition_ids: Array.isArray(i.condition_ids)
            ? [...i.condition_ids]
            : [],
        }));
      }
    },
  },

  methods: {
    formatScore(score) {
      const n = Number(score);
      if (Number.isNaN(n)) return score;
      return n <= 1 ? `${Math.round(n * 100)}%` : `${Math.round(n)}%`;
    },
    removeItem(index) {
      this.localItems.splice(index, 1);
    },
    handleCancel() {
      this.$emit("cancel");
      this.$emit("input", false);
    },
    handleConfirm() {
      this.$emit("confirm", this.localItems.map((i) => ({ ...i })));
    },
  },
};
</script>

<style scoped>
.ds-dialog {
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(31, 77, 63, 0.18),
    0 4px 12px rgba(0, 0, 0, 0.06) !important;
}
.close-btn {
  position: relative;
  z-index: 1;
}
.close-btn:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
}
</style>
